import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
  type FunctionInvokeOptions,
} from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const SESSION_EXPIRED_EVENT = "athr:session-expired";

type FunctionError = FunctionsHttpError | FunctionsFetchError | FunctionsRelayError | null;
type InvokeResult<T> = { data: T | null; error: FunctionError };

type AuthDiagnosticSnapshot = {
  functionName: string;
  phase: "initial_error" | "refresh_failed" | "retry_error";
  hasSession: boolean;
  userId: string | null;
  expiresAt: number | null;
  now: number;
  status: number | null;
  errorText: string;
};

let lastExpiredDispatchAt = 0;

const getFunctionStatusCode = (error: FunctionError): number | null => {
  if (!error) return null;
  if (!(error instanceof FunctionsHttpError)) return null;
  const context = error.context;
  return context instanceof Response ? context.status : null;
};

const getFunctionErrorText = async (error: FunctionError): Promise<string> => {
  if (!error) return "";

  const baseMessage = "message" in error && typeof error.message === "string" ? error.message : "";
  if (!(error instanceof FunctionsHttpError)) {
    return baseMessage.toLowerCase();
  }

  const context = error.context;
  if (!(context instanceof Response)) {
    return baseMessage.toLowerCase();
  }

  try {
    const payload = (await context.clone().json()) as { error?: string; message?: string; details?: string };
    return [baseMessage, payload.error, payload.message, payload.details]
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      .join(" ")
      .toLowerCase();
  } catch {
    return baseMessage.toLowerCase();
  }
};

const shouldRetryAuth = async (error: FunctionError): Promise<boolean> => {
  if (!error) return false;
  const status = getFunctionStatusCode(error);
  if (status === 401) return true;

  const message = await getFunctionErrorText(error);
  return (
    message.includes("invalid jwt") ||
    message.includes("jwt expired") ||
    message.includes("missing authorization") ||
    message.includes("unauthorized")
  );
};

const dispatchSessionExpired = () => {
  const now = Date.now();
  if (now - lastExpiredDispatchAt < 5000) return;
  lastExpiredDispatchAt = now;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

const hasActiveSession = async (): Promise<boolean> => {
  const { data } = await supabase.auth.getSession();
  return Boolean(data.session);
};

const withAccessTokenHeader = (
  options: FunctionInvokeOptions | undefined,
  accessToken: string | null | undefined,
): FunctionInvokeOptions | undefined => {
  if (!accessToken) return options;

  const headers = new Headers((options?.headers as HeadersInit | undefined) ?? undefined);
  headers.set("Authorization", `Bearer ${accessToken}`);

  return {
    ...(options ?? {}),
    headers,
  };
};

const logAuthDiagnostic = (snapshot: AuthDiagnosticSnapshot) => {
  if (!import.meta.env.DEV || typeof window === "undefined") return;

  const payload = {
    ...snapshot,
    expiresInMs: snapshot.expiresAt ? snapshot.expiresAt - snapshot.now : null,
  };

  (window as Window & { __athrAuthDiagnostics?: AuthDiagnosticSnapshot[] }).__athrAuthDiagnostics ??= [];
  (window as Window & { __athrAuthDiagnostics?: AuthDiagnosticSnapshot[] }).__athrAuthDiagnostics!.push(snapshot);

  console.warn("[Athr auth diagnostic]", payload);
};

export const invokeWithSessionRetry = async <T = unknown>(
  functionName: string,
  options?: FunctionInvokeOptions,
): Promise<InvokeResult<T>> => {
  const { data: initialSessionData } = await supabase.auth.getSession();
  const initialSession = initialSessionData.session;
  
  const invokeOptions = withAccessTokenHeader(options, initialSession?.access_token);

  let result = await supabase.functions.invoke<T>(
    functionName,
    invokeOptions,
  );
  const initialShouldRetry = await shouldRetryAuth(result.error);
  if (!initialShouldRetry) {
    return result;
  }

  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session) {
    dispatchSessionExpired();
    return result;
  }

  result = await supabase.functions.invoke<T>(
    functionName,
    withAccessTokenHeader(options, data.session.access_token),
  );
  const retryShouldRetry = await shouldRetryAuth(result.error);
  if (retryShouldRetry) {
    // Some functions can return 401 for scope/claim mismatch while session is still valid.
    // Only force a sign-out flow when we confirm the session is missing.
    if (!(await hasActiveSession())) {
      dispatchSessionExpired();
    }
  }

  return result;
};

export const getFunctionErrorDetails = async (error: unknown): Promise<string | null> => {
  if (!(error instanceof FunctionsHttpError)) return null;

  const { context } = error;
  if (!(context instanceof Response)) return null;

  try {
    const payload = (await context.clone().json()) as {
      error?: string;
      message?: string;
      step?: string;
      details?: string;
    };
    const parts = [payload.error || payload.message, payload.step ? `step: ${payload.step}` : null, payload.details]
      .filter(Boolean)
      .join(" | ");
    return parts || null;
  } catch {
    return null;
  }
};
